import { Component, computed, DestroyRef, inject, input, ChangeDetectionStrategy } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { TacticType } from "@front/m19-api-client";
import { SdStrategyEx } from "@front/m19-models/strategy/SdStrategyEx";
import { AccountSelectionService, Constant } from "@front/m19-services";
import { IButtonDirective, IIconComponent, ModalService, ITooltipDirective } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import {
  SdTargetingModalCreationComponent,
  SdTargetingModalCreationData,
} from "@m19-board/shared/sd-targeting-modal-creation/sd-targeting-modal-creation.component";
import { SdTargetingComponent } from "@m19-board/sponsored-display/sd-targeting/sd-targeting.component";
import { map } from "rxjs";

@Component({
  selector: "app-sd-strategy-targeting-list",
  imports: [SdTargetingComponent, TranslocoDirective, IButtonDirective, IIconComponent, ITooltipDirective],
  templateUrl: "./sd-strategy-targeting-list.component.html",
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SdStrategyTargetingListComponent {
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly modalService = inject(ModalService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translocoService = inject(TranslocoService);

  private readonly MAX_TARGETINGS_PER_SD_STRATEGY = Constant.maxTargetingsPerSdStrategy;

  strategy = input<SdStrategyEx>();

  isReadOnly = toSignal(this.accountSelectionService.readOnlyMode$, { requireSync: true });
  organizationId = toSignal(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      takeUntilDestroyed(this.destroyRef),
      map((am) => am.resourceOrganizationId!),
    ),
    { requireSync: true },
  );

  private readonly targetingCount = computed(() => {
    const strategy = this.strategy();
    if (!strategy) return 0;

    return (
      (strategy.disableOtherQueries ? 0 : 1) +
      strategy.tactics.filter((x) => x.tacticType == TacticType.LEGACY).length +
      this.audiences().length
    );
  });

  private readonly blacklistTactics = computed(() => {
    const strategy = this.strategy();
    if (!strategy) return [];

    return strategy.tactics.filter((x) => x.tacticType === TacticType.BLACKLIST);
  });

  private readonly nonBlacklistTactics = computed(() => {
    const strategy = this.strategy();
    if (!strategy) return [];

    return strategy.tactics.filter((x) => x.tacticType !== TacticType.BLACKLIST);
  });

  readonly audiences = computed(() => {
    const strategy = this.strategy();
    if (!strategy) return [];

    return strategy.audienceTargetings;
  });

  readonly targetings = computed(() => [...this.blacklistTactics(), ...this.nonBlacklistTactics()]);

  readonly isTargetingCountReached = computed(() => this.targetingCount() >= this.MAX_TARGETINGS_PER_SD_STRATEGY);
  readonly isOnlyOneTargeting = computed(() => this.targetingCount() === 1);

  createTargeting() {
    if (!this.strategy() || this.isTargetingCountReached()) {
      return;
    }

    this.modalService.openModal<SdTargetingModalCreationData, void>(SdTargetingModalCreationComponent, {
      size: "md",
      modalTitle: this.translocoService.translate("sd-targeting-add-popup.add_a_new_targeting"),
      closeMode: "buttonOnly",
      data: {
        strategy: this.strategy()!,
        organizationId: this.organizationId(),
      },
    });
  }
}
